"use client";

import React from "react";
import { BookOpen, GraduationCap, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pathways = [{
  id: "primary",
  title: "Tiny Explorers",
  description: "Where language learning feels like play! Our group sessions are filled with magical stories, songs, and games that get little ones speaking Spanish naturally from day one in a joyful environment.",
  subjects: [" Puppet Stories", " Sing-Alongs", " Color Games", " Basic Greetings", " Animal Friends", " Art & Crafts", " Action Songs"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/primary_education_students.png",
  icon: BookOpen
}, {
  id: "secondary",
  title: "Bold Adventurers",
  description: "A personalized journey for curious minds. These 1-on-1 lessons focus on building fluency and confidence through exciting missions, creative projects, and dedicated teacher attention.",
  subjects: [" Live Missions", " Creative Writing", " Cultural Quests", " Advanced Speaking", " Grammar Games", " Reading Journeys", " Digital Storytelling"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/secondary_education_students.png",
  icon: GraduationCap
}];
export default function AcademicPathways() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="135609" id="explore-our-programs" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`relative rounded-[3rem] bg-primary px-6 py-16 sm:px-12 sm:py-24 lg:px-16 shadow-2xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="absolute inset-0 overflow-hidden rounded-[3rem] pointer-events-none">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-secondary/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 flex flex-col items-center text-center mb-12 sm:mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-serif font-bold text-primary-foreground tracking-tight mb-6">
              Explore Our Programs
            </h2>
            <p className="text-xl sm:text-2xl text-primary-foreground/90 max-w-3xl font-sans leading-relaxed">
              Our lessons are designed for young learners who need energy, encouragement, 
              creativity, and real interaction. We make Spanish feel exciting and fun!
            </p>
          </div>

          <Tabs defaultValue="primary" className="w-full max-w-6xl mx-auto">
            <div className="flex justify-center mb-12">
              <TabsList className="bg-primary-foreground/20 p-2 rounded-2xl border border-primary-foreground/20 backdrop-blur-md">
                {pathways.map((pathway, index) => {
                const Icon = pathway.icon;
                return <TabsTrigger key={pathway.id} value={pathway.id} data-index={index} className="rounded-xl px-8 py-4 text-lg font-bold text-primary-foreground/80 data-[state=active]:bg-primary-foreground data-[state=active]:text-primary data-[state=active]:shadow-lg transition-all duration-300">
                      <Icon className="w-6 h-6 mr-2" />
                      {index === 0 ? "Group Classes" : "1-on-1 Adventures"}
                    </TabsTrigger>;
              })}
              </TabsList>
            </div>

            {pathways.map((pathway, index) => <TabsContent key={pathway.id} value={pathway.id} data-index={index} className="data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-6 duration-700 outline-none">
                <Card className="flex flex-col lg:flex-row overflow-hidden bg-white/10 border-white/20 text-primary-foreground shadow-2xl backdrop-blur-md p-0 gap-0 rounded-[2.5rem]">
                  <div className="lg:w-2/5 relative min-h-[350px] lg:min-h-[550px]">
                    <img src={pathway.image} alt={pathway.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-primary/30 lg:to-primary/90" />
                  </div>
                  
                  <div className="lg:w-3/5 p-10 sm:p-12 lg:p-16 flex flex-col justify-center">
                    <CardHeader className="p-0 mb-8">
                      <CardTitle className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-primary-foreground">
                        {pathway.title}
                      </CardTitle>
                      <CardDescription className="text-lg sm:text-xl text-primary-foreground/90 mt-6 leading-relaxed font-sans">
                        {pathway.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="p-0 mb-10">
                      <h4 className="text-sm font-bold text-primary-foreground/70 uppercase tracking-widest mb-6">
                        What's Inside
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {pathway.subjects.map((subject, subjectIndex) => <Badge key={subjectIndex} data-index={subjectIndex} variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30 border-transparent px-4 py-2 text-base font-semibold rounded-full transition-all">
                            {subject}
                          </Badge>)}
                      </div>
                    </CardContent>
                    
                    <CardFooter className="p-0 mt-auto">
                      <Button asChild size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105 font-bold rounded-full px-10 py-7 text-lg shadow-xl transition-all">
                        <Link to="/OurClassesPage">
                          Start the Adventure
                          <ArrowRight className="ml-2 w-6 h-6" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </div>
                </Card>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}